<template>
  <div class="query-params-manager">
    <el-dropdown trigger="hover" @command="handleSelect" placement="bottom-start">
      <el-button type="text">
        <span class="dropdown-btn">
          <span class="condition-name">{{ currentName }}</span
          ><i class="el-icon-arrow-down el-icon--right"></i>
        </span>
      </el-button>
      <el-dropdown-menu slot="dropdown" v-loading="loading">
        <el-dropdown-item v-if="!conditions.length" command="" class="query-dropdown-item">
          <div class="dropdown-item-content">
            <span class="item-name empty-tip">暂无保存的查询条件</span>
          </div>
        </el-dropdown-item>
        <template v-else>
          <el-dropdown-item
            v-for="item of conditions"
            :key="item.id"
            :command="item.id"
            style="padding: 2px 0 2px 10px"
            class="query-dropdown-item"
          >
            <div class="dropdown-item-content">
              <span class="item-name">{{ item.name }}</span>
              <i class="el-icon-edit edit-btn" @click.stop="handleDropdownEdit(item)"></i>
              <i class="el-icon-close delete-btn" @click.stop="handleDropdownDelete(item)"></i>
            </div>
          </el-dropdown-item>
        </template>
      </el-dropdown-menu>
    </el-dropdown>

    <el-button type="text" v-if="conditions.length < maxConditions" @click="handleSaveClick" style="margin-left: 10px">
      保存查询条件
    </el-button>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { MessageBox } from 'element-ui'
import { Message } from 'view-design'
import { getQueryCondition, addQueryCondition, modifyQueryCondition, delQueryCondition } from '@/api/ptom.data.model'
import { isArray } from '@/common/js/utils'

export default defineComponent({
  name: 'QueryParamsManager',
  props: {
    menuId: {
      type: String,
      required: true
    },
    maxConditions: {
      type: Number,
      default: 20
    }
  },
  emits: ['select', 'request-save'],
  setup(props, { emit, expose }) {
    // State
    const conditions = ref([])
    const currentId = ref(null)
    const loading = ref(false)
    const editName = ref('')

    // Computed
    const currentName = computed(() => {
      if (typeof currentId.value === 'number' && currentId.value > -1) {
        const found = conditions.value.find((c) => c.id === currentId.value)
        if (found) return `查询条件：${found.name}`
      }
      return '选择常用查询'
    })

    // Methods
    const fetchConditions = async () => {
      loading.value = true
      try {
        const res = await getQueryCondition({ menuId: props.menuId })
        loading.value = false
        if (isArray(res)) {
          conditions.value = res
        } else {
          // Fallback or empty
        }
      } catch (error) {
        loading.value = false
        conditions.value = []
      }
    }

    const handleSelect = (id) => {
      if (!id) return
      const cond = conditions.value.find((c) => c.id === id)
      if (cond) {
        currentId.value = id
        emit('select', cond)
      }
    }

    const handleSaveClick = () => {
      // Notify parent to provide current params
      emit('request-save')
    }

    const openSaveDialog = (params) => {
      MessageBox.prompt('请输入名称', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /^.{1,40}$/,
        inputValue: editName.value || '',
        inputPlaceholder: '请输入名称，最大40个字符',
        inputErrorMessage: '请输入名称，最大40个字符'
      })
        .then(({ value }) => {
          if (editName.value) {
            modifyQueryCondition({
              list: conditions.value.map((c) => {
                if (c.name === editName.value) {
                  return { id: c.id, name: value }
                }
                return { id: c.id, name: c.name }
              })
            })
              .then((data) => {
                if (data) {
                  fetchConditions()
                  Message.success('保存成功')
                }
              })
              .finally(() => {
                editName.value = ''
              })
          } else {
            const paramStr = params ? JSON.stringify(params) : ''
            addQueryCondition({
              menuId: props.menuId,
              name: value,
              conditionInfo: paramStr
            }).then((data) => {
              if (data) {
                fetchConditions()
                Message.success('保存成功')
              }
            })
          }
        })
        .catch(() => {})
    }

    const deleteSavedCondition = (id) => {
      delQueryCondition({ id }).then((data) => {
        if (data) {
          fetchConditions()
          Message.success('删除成功')
        }
      })
    }

    const handleDropdownEdit = (item) => {
      editName.value = item.name
      openSaveDialog()
    }
    const handleDropdownDelete = (item) => {
      MessageBox.confirm(`确认删除查询条件 "${item.name}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          deleteSavedCondition(item.id)
        })
        .catch(() => {})
    }

    expose({ openSaveDialog })

    onMounted(() => {
      fetchConditions()
    })

    return {
      conditions,
      currentId,
      loading,
      currentName,
      handleSelect,
      handleSaveClick,
      handleDropdownDelete,
      handleDropdownEdit
    }
  }
})
</script>

<style lang="scss">
.query-dropdown-item {
  &.el-dropdown-menu__item {
    padding: 0 3px;
  }
}
</style>
<style scoped lang="scss">
.query-params-manager {
  display: inline-block;

  .dropdown-btn {
    display: flex;
    align-items: center;
  }
}
.condition-name {
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dropdown-item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 150px;
}
.item-name {
  margin-right: 8px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &.empty-tip {
    font-size: 12px;
    color: #9b9b9b;
  }
}
.delete-btn,
.edit-btn {
  font-size: 14px;
  color: #909399;
  cursor: pointer;
  padding: 2px;
}
.delete-btn:hover {
  color: #f56c6c;
  background-color: #fef0f0;
  border-radius: 50%;
}
</style>
